/**
 * cirrus_layer — the HIGH ÉTAGE as a plane-projected 2D layer in the sky
 * background (2026-09-25, realism §8 R6b — the technique three r186 gave its
 * `Sky` clouds).
 *
 * WHY A PLANE. The background sphere is drawn camera-locked at infinity, so a
 * finite-altitude VOLUME composited into it reads as a band pinned to the view
 * (the 2026 Phase C lesson). Cirrus / cirrostratus / altostratus at 7–10 km are
 * a THIN sheet: intersect the view ray with the plane y = H in ABSOLUTE
 * coordinates, `p = camAbs.xz + dir.xz · (H − camAbs.y) / dir.y`, and the sheet
 * has exactly the parallax a real one has (none you can walk off, the right
 * foreshortening toward the horizon). The 3.2 km mesh "cirrus" archetype it
 * replaces was a cumulus-height imitation.
 *
 * SHAPE: a 2D fBm (MaterialX fractal noise), four octaves with per-octave drift,
 * stretched 3:1 ALONG the upper wind (cirrus is hair combed by the jet); the
 * coverage (the formation's — a fair sky carries sparse streaks, a warm front's
 * approach a thickening cirrostratus veil) thresholds it.
 * LIGHT: Beer–powder through the sheet's optical depth, a forward HG lobe (the
 * silver lining near the sun), the sun's colour at the sheet's altitude (little
 * air above 8 km: white until the sun is low, then gold and pink — lit long
 * after the ground is dark), the sky's zenith radiance as ambient, and an
 * aerial extinction that sinks the far sheet into the horizon's colour.
 *
 * The node is built ONCE (SkyModule's composite); every input is a uniform.
 * `cirrusCoverageFor` is the pure rule the harness pins.
 */
import { float, vec2, vec3, dot, normalize, max, exp, mix, smoothstep, pow, mx_fractal_noise_float } from 'three/tsl';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export interface CirrusUniforms {
  /** Camera position in ABSOLUTE metres (render + origin). */
  camAbs: N;
  /** The sheet's altitude (m, absolute). */
  height: N;
  /** Seconds (the drift clock). */
  time: N;
  /** Upper-wind direction (unit XZ). */
  wind: N;
  /** Coverage 0..1 (the formation's). */
  coverage: N;
  /** World direction TO the sun. */
  sunDir: N;
  /** The sun's radiance scale at the sheet (scene units, colour included). */
  sunLight: N;
  /** Ambient: the sky's zenith radiance (scene units). */
  ambient: N;
  /** The horizon's radiance the far sheet sinks into (scene units). */
  horizon: N;
}

/** Coverage of the high étage by the weather regime (the warm-front sequence): sparse on a fair day, a veil ahead of rain. */
export function cirrusCoverageFor(regime: 'fair' | 'humid' | 'wet', cover: number): number {
  const c = Math.max(0, Math.min(1, cover));
  if (regime === 'wet') return 0.55;                 // behind the deck; what shows through its gaps is a grey sheet
  if (regime === 'humid') return 0.45 + 0.35 * c;    // thickening cirrostratus / altostratus
  return 0.18 + 0.25 * c;                            // fair: scattered streaks
}

/** The layer: premultiplied radiance + coverage alpha for a view direction. */
export function buildCirrusLayer(viewDir: N, u: CirrusUniforms): { rgb: N; a: N } {
  const d = vec3(viewDir);
  const up = max(d.y, float(0.004));
  const t = max(float(u.height).sub(u.camAbs.y), float(100)).div(up);
  const p = vec2(u.camAbs.x, u.camAbs.z).add(vec2(d.x, d.z).mul(t));
  // Wind frame: along-wind stretched 3:1 (streaks), each octave drifting on its own.
  const w = normalize(vec2(u.wind).add(vec2(1e-4, 0)));
  const along = dot(p, w), across = dot(p, vec2(w.y.negate(), w.x));
  const drift = float(u.time).mul(9); // m/s at the jet — the sheet visibly creeps
  const q = vec3(along.add(drift).div(9000), across.div(3000), float(u.time).mul(0.004));
  const n0 = mx_fractal_noise_float(q, 4, 2.1, 0.5, 1).mul(0.5).add(0.5);
  const n1 = mx_fractal_noise_float(q.mul(3.1).add(vec3(7.3, 1.9, float(u.time).mul(0.011))), 3, 2.2, 0.5, 1).mul(0.5).add(0.5);
  const n = n0.mul(0.72).add(n1.mul(0.28));
  const cov = float(u.coverage);
  const density = smoothstep(float(1).sub(cov), float(1.25).sub(cov.mul(0.9)), n).mul(cov.mul(0.6).add(0.4));
  const od = density.mul(1.4);                        // a thin ice sheet: optical depth ≲ 1.4
  const T = exp(od.negate());
  const powder = float(1).sub(exp(od.mul(-2)));
  const mu = dot(d, normalize(vec3(u.sunDir)));
  const g = float(0.6), gg = g.mul(g);
  const hg = float(1).sub(gg).div(pow(max(float(1).add(gg).sub(g.mul(mu).mul(2)), float(1e-4)), float(1.5))).mul(1 / (4 * Math.PI));
  const phase = hg.mul(0.8).add(1 / (4 * Math.PI) * 0.2);
  const lit = vec3(u.sunLight).mul(phase.mul(float(1).sub(T)).mul(powder.mul(0.6).add(0.4)).mul(6))
    .add(vec3(u.ambient).mul(float(1).sub(T)).mul(0.9));
  // Aerial extinction: the far sheet (low on the sky) sinks into the horizon.
  const far = float(1).sub(smoothstep(float(0.03), float(0.32), d.y));
  const rgb = mix(lit, vec3(u.horizon).mul(float(1).sub(T)), far.mul(0.7));
  const a = float(1).sub(T).mul(smoothstep(float(0.005), float(0.06), d.y));
  return { rgb: rgb.mul(smoothstep(float(0.005), float(0.06), d.y)), a };
}
